import { Table, TableBody, TableCell, TableRow } from "@mui/material";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import PATEditButton from "../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useDispatch, useSelector } from "react-redux";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import {
	getCodeId,
	getRowsPerPageOptions,
	getServiceStatus,
	StatusBadge,
} from "../../utils/functions";
import { Box } from "@mui/system";
import { useTheme } from "@emotion/react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { AMSModules, paginationStyle } from "../../utils/constants";
import VehiclesTableToolbar from "./VehiclesTableToolbar";
import AddEditVehicleDialog from "./AddEditVehicle";
import PATUpButton from "../../components/Buttons/PATUpButton";
import PATDownButton from "../../components/Buttons/PATDownButton";
import CheckInOutVehicleDialog from "./CheckInOutVehicleDialog";
import {
	createVehicle,
	deleteVehicle,
	getVehicles,
	updateVehicle,
} from "../../reducers/VehiclesReducer";
import { createInspection } from "../../reducers/InspectionsReducer";
import { notify } from "../../reducers/NotifierReducer";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import {
	createVehicleAssignmentHistory,
	findActiveVehicleAssignment,
	getEmployees,
	getFundingSources,
	getWorkOrders,
	updateVehicleAssignmentHistory,
} from "../../utils/commonServices";
import {
	getMakeOptions,
	getModelOptions,
	getYearOptions,
	updateVehicleData,
} from "./useVehiclesData";
import {
	getInspectionTypeOptions,
	getChecklistItems,
} from "../Inspections/useInspectionsData";
import AddEditInspectionDialog from "../Inspections/AddEditInspectionDialog";
import VehicleDetail from "./VehicleDetails";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

export const initialFilterState = {
	type: "all",
	status: [],
	service: [],
	make: [],
	model: [],
	year: [],
	division: [],
	perPersonnelId: [],
	fundingSourceId: [],
};

const Vehicles = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();
	const { vehicles, loading, total } = useSelector(
		(state) => state?.vehicles,
	);
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);
	const dictionary = useSelector((state) => state.dictionary.dictionary);
	const {
		codePerVehicleStatus,
		codePerDepartment,
		codePerVehicleServiceRequirements: serviceRequirements,
		codePerInspectionTypes,
	} = dictionary || {};

	const vehicleStatus = codePerVehicleStatus?.map((status) => ({
		label: status?.Code,
		value: status?.Code,
	}));
	const departmentOptions = codePerDepartment?.map((dept) => ({
		label: dept?.Code,
		value: dept?.Code,
	}));

	const [search, setSearch] = useState("");
	const [openDialog, setOpenDialog] = useState(false);
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo.vehicles || initialFilterState,
	);
	const [showCheckout, setShowCheckout] = useState(false);
	const [checkoutMode, setCheckoutMode] = useState("out");
	const [checkoutVehicle, setCheckoutVehicle] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedId, setSelectedId] = useState(null);
	const [selectedVehicle, setSelectedVehicle] = useState(null);
	const [employees, setEmployees] = useState([]);
	const [fundingSources, setFundingSources] = useState([]);
	const [workOrders, setWorkOrders] = useState([]);
	const [makeOptions, setMakeOptions] = useState([]);
	const [modelOptions, setModelOptions] = useState([]);
	const [yearOptions, setYearOptions] = useState([]);
	const [selectedVehicleDetail, setSelectedVehicleDetail] = useState(null);

	// Inspection-related state
	const [openInspectionDialog, setOpenInspectionDialog] = useState(false);
	const [inspectionPreFill, setInspectionPreFill] = useState(null);
	const [inspectionTypeOptions, setInspectionTypeOptions] = useState([]);
	const [checklistItems, setChecklistItems] = useState([]);

	const currentAgency = useRef(null);

	const handleSaveData = async () => {
		const [
			empList,
			fundingList,
			workOrderList,
			makeList,
			modelList,
			yearList,
			typeOptions,
			checklist,
		] = await Promise.all([
			getEmployees(userAgency, true),
			getFundingSources(userAgency, true),
			getWorkOrders(userAgency),
			getMakeOptions(userAgency),
			getModelOptions(userAgency),
			getYearOptions(userAgency),
			getInspectionTypeOptions(dictionary),
			getChecklistItems(userAgency),
		]);

		setYearOptions(yearList);
		setModelOptions(modelList);
		setMakeOptions(makeList);
		setEmployees(empList);
		setWorkOrders(workOrderList);
		setFundingSources(fundingList);
		setInspectionTypeOptions(typeOptions);
		setChecklistItems(checklist);
	};

	useEffect(() => {
		if (selectedVehicleDetail && vehicles.length > 0) {
			const updatedVehicle = vehicles.find(
				(v) => v.id === selectedVehicleDetail.id,
			);
			if (updatedVehicle && updatedVehicle !== selectedVehicleDetail) {
				setSelectedVehicleDetail(updatedVehicle);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vehicles]);

	useEffect(() => {
		handleSaveData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (userAgency && dictionary) {
			handleSaveData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency, dictionary]);

	useEffect(() => {
		if (!settingsLoaded || !isAuthenticated) return;

		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}

			dispatch(
				getVehicles(
					page,
					pageSize,
					field,
					sort,
					search,
					isAgencyChanged ? initialFilterState : filters,
					userAgency,
					serviceRequirements,
					workOrders,
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		search,
		filters,
		settingsLoaded,
		isAuthenticated,
		userAgency,
	]);

	const refreshList = useCallback(() => {
		// Get current sort model
		const { field, sort } = sortModel[0] || {};
		if (field && sort) {
			dispatch(
				getVehicles(
					page,
					pageSize,
					field,
					sort,
					search,
					filters,
					userAgency,
					serviceRequirements,
					workOrders,
				),
			);
		}
	}, [
		dispatch,
		page,
		pageSize,
		sortModel,
		search,
		filters,
		userAgency,
		serviceRequirements,
		workOrders,
	]);

	const vehicleSvcStatus = useMemo(() => {
		const map = {};
		vehicles.forEach((v) => {
			const statuses = (v.serviceRequirements || [])
				.map((reqId) => {
					const req = serviceRequirements?.find(
						(sr) => sr.ptsCodeId === reqId || sr.id === reqId,
					);
					return req ? getServiceStatus(v, req, workOrders) : null;
				})
				.filter(Boolean);

			const worst =
				statuses.find((s) => s.status === "Overdue") ||
				statuses.find((s) => s.status === "Due Soon") ||
				statuses[0] ||
				null;

			map[v.id] = { worst, statuses };
		});
		return map;
	}, [vehicles, serviceRequirements, workOrders]);

	// Vehicle options for inspection dialog
	const vehicleOptionsForInspection = useMemo(() => {
		return vehicles.map((v) => ({
			value: v.id,
			label: `${v.unitNumber} — ${v.year} ${v.make} ${v.model}`,
			unitNumber: v.unitNumber,
			make: v.make,
			model: v.model,
			year: v.year,
			status: v.status,
			odometer: v.odometer,
		}));
	}, [vehicles]);

	const baseColumns = useMemo(
		() => [
			{
				field: "unitNumber",
				headerName: "Unit#",
				width: 80,
				sortable: true,
			},
			{
				field: "year/make/model",
				headerName: "YEAR / MAKE / MODEL",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return <>{`${row?.year} ${row?.make} ${row?.model}`}</>;
				},
			},
			{
				field: "vin",
				headerName: "VIN",
				width: 150,
				sortable: true,
			},
			{
				field: "status",
				headerName: "Status",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return <StatusBadge status={row?.status} />;
				},
			},
			{
				field: "odometer",
				headerName: "ODOMETER",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return <>{row?.odometer} mi</>;
				},
			},
			{
				field: "personnelInfo",
				headerName: "Assigned To",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return (
						<>{`${row?.["personnelInfo.FirstName"] || ""} ${row?.["personnelInfo.LastName"] || ""}`}</>
					);
				},
			},
			{
				field: "division",
				headerName: "DIVISION",
				width: 150,
				sortable: true,
			},
			{
				field: "service",
				headerName: "SERVICE",
				width: 100,
				sortable: true,
				renderCell: ({ row }) => {
					const rowStatus = vehicleSvcStatus[row.id];
					const worst = rowStatus?.worst;
					if (worst) {
						const cls =
							worst.status === "Overdue" ? "overdue" : "due-soon";
						return (
							<span className={`svc-badge ${cls}`}>
								{worst.status}
							</span>
						);
					}
					if (row?.serviceRequirements?.length > 0) {
						return <span className="svc-badge ok">OK</span>;
					}
					return (
						<span className="text3" style={{ fontSize: 11 }}>
							—
						</span>
					);
				},
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 150,
				align: "center",
				sortable: false,
				renderCell: ({ row }) =>
					row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										{row?.status !== "Retired" &&
											(row?.perPersonnelId ? (
												<PATDownButton
													tooltipText="Check In"
													onClick={() => {
														setShowCheckout(true);
														setCheckoutMode("in");
														setCheckoutVehicle(row);
													}}
													size="20px"
												/>
											) : (
												<PATUpButton
													tooltipText="Check Out"
													onClick={() => {
														setShowCheckout(true);
														setCheckoutMode("out");
														setCheckoutVehicle(row);
													}}
													size="20px"
												/>
											))}
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Vehicle"
											onClick={() => {
												setSelectedVehicle(row);
												setOpenDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Vehicle"
											onClick={() => {
												setSelectedId(row.id);
												setSelectedVehicle(row);
												setOpenDeleteDialog(true);
											}}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null,
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history, vehicleSvcStatus],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["vehiclesTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["vehiclesTableColumnOrder"]),
				);
			}

			if (settingsMap["vehiclesTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["vehiclesTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["vehiclesTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(settingsMap["vehiclesTableColumnVisibility"]),
				);
			}

			if (settingsMap["vehiclesTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["vehiclesTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"vehiclesTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"vehiclesTableColumnOrder",
						JSON.stringify(newColumnOrder),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => {
			return {
				field: col.field,
				width: col.width,
			};
		});
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"vehiclesTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"vehiclesTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.Vehicles, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({ type: AMSModules.Vehicles, payload: initialFilterState });
	};

	const handleSaveVehicle = async (payload) => {
		const serviceReqIds = payload.serviceRequirements
			?.map((req) => {
				const findId = getCodeId(req, serviceRequirements);
				if (findId) return findId;
				return undefined;
			})
			.filter(Boolean);

		const data = {
			...payload,
			statusId: getCodeId(payload?.status, codePerVehicleStatus),
			divisionId: getCodeId(payload?.division, codePerDepartment),
		};
		try {
			if (selectedVehicle) {
				await dispatch(
					updateVehicle(
						selectedVehicle.id,
						data,
						serviceReqIds,
						userAgency,
						selectedVehicle,
					),
				);
			} else {
				await dispatch(
					createVehicle(
						data,
						serviceReqIds,
						userAgency,
						userData.user.ptsUserID,
					),
				);
			}
			setOpenDialog(false);
			setSelectedVehicle(null);
			refreshList();
		} catch (error) {
			dispatch(notify("Failed to save vehicle", "error"));
		}
	};

	// Updated handleCheckInOut to support inspection flow
	const handleCheckInOut = async (
		id,
		payload,
		inspectionData = {},
		oldData = {},
	) => {
		try {
			const response = await updateVehicleData(
				id,
				payload,
				userAgency,
				oldData,
			);
			if (response) {
				if (checkoutMode === "out" && payload.perPersonnelId) {
					// CHECK-OUT: Create new assignment history record
					try {
						await createVehicleAssignmentHistory({
							vehicleId: id,
							perPersonnelId: payload.perPersonnelId,
							assignedDate: new Date()
								.toISOString()
								.split("T")[0], // YYYY-MM-DD format
							unassignedDate: null,
							assignedBy: userData?.user?.Username
								? String(userData.user.Username)
								: null,
							notes: payload.notes || null,
							agencyId: userAgency,
							isDeleted: false,
						});
					} catch (historyError) {
						console.error(
							"Failed to create assignment history:",
							historyError,
						);
						// Don't fail the whole operation, just log the error
					}
				} else if (checkoutMode === "in") {
					// CHECK-IN: Update existing assignment history with unassignedDate
					try {
						const activeAssignment =
							await findActiveVehicleAssignment(id, userAgency);

						if (activeAssignment) {
							await updateVehicleAssignmentHistory(
								activeAssignment.id,
								{
									unassignedDate: new Date()
										.toISOString()
										.split("T")[0],
									notes: activeAssignment.notes
										? `${activeAssignment.notes}\n[Check-in] ${payload.notes || ""}`
										: payload.notes || null,
								},
							);
						}
					} catch (historyError) {
						console.error(
							"Failed to update assignment history:",
							historyError,
						);
					}
				}

				const { field, sort } = sortModel[0] || {};
				if (field && sort) {
					dispatch(
						getVehicles(
							page,
							pageSize,
							field,
							sort,
							search,
							filters,
							userAgency,
						),
					);
				}
				dispatch(
					notify(
						`Vehicle ${checkoutMode === "out" ? "checked out" : "checked in"} successfully`,
						"success",
					),
				);

				// Close checkout dialog
				setShowCheckout(false);
				setCheckoutVehicle(null);

				// If performInspection is true, open inspection dialog
				if (
					inspectionData?.performInspection &&
					inspectionData?.inspectionPreFill
				) {
					const preFill = inspectionData.inspectionPreFill;

					// Set pre-fill data for inspection dialog
					setInspectionPreFill({
						vehicleId: preFill.vehicleId,
						perPersonnelId: preFill.perPersonnelId
							? String(preFill.perPersonnelId)
							: "",
						odometer: preFill.odometer || "",
						// Pass vehicle info for display
						vehicleInfo: preFill.vehicleInfo,
					});

					// Open inspection dialog after a small delay to ensure checkout dialog is closed
					setTimeout(() => {
						setOpenInspectionDialog(true);
					}, 100);
				}
			}
		} catch (error) {
			dispatch(notify("Failed to save vehicle", "error"));
		}
	};

	// Handle save inspection from check-in/out flow
	const handleSaveInspection = async (payload) => {
		try {
			const typeId = getCodeId(payload.type, codePerInspectionTypes);

			const data = {
				vehicleId: payload.vehicleId,
				perPersonnelId: payload.perPersonnelId,
				type: payload.type,
				typeId,
				date: payload.date,
				odometer: payload.odometer,
				overallStatus: payload.overallStatus,
				notes: payload.notes,
			};

			await dispatch(
				createInspection(
					data,
					payload.responses || [],
					payload.photos || [],
					userAgency,
					userData.user.ptsUserID,
				),
			);

			setOpenInspectionDialog(false);
			setInspectionPreFill(null);
			dispatch(notify("Inspection saved successfully", "success"));

			// Refresh vehicles to update odometer if changed
			const { field, sort } = sortModel[0] || {};
			if (field && sort) {
				dispatch(
					getVehicles(
						page,
						pageSize,
						field,
						sort,
						search,
						filters,
						userAgency,
					),
				);
			}
		} catch (error) {
			dispatch(notify("Failed to save inspection", "error"));
		}
	};

	const handleDelete = async () => {
		try {
			await dispatch(
				deleteVehicle(
					selectedId,
					userData.user.ptsUserID,
					selectedVehicle,
				),
			);
			setOpenDeleteDialog(false);
			setSelectedId(null);
			setSelectedVehicle(null);
		} catch (error) {
			dispatch(notify("Failed to delete vehicle", "error"));
		}
	};

	return (
		<>
			{selectedVehicleDetail ? (
				<VehicleDetail
					vehicle={selectedVehicleDetail}
					refreshList={refreshList}
					onBack={() => setSelectedVehicleDetail(null)}
					onEdit={(v) => {
						setSelectedVehicle(v);
						setOpenDialog(true);
						setSelectedVehicleDetail(null);
					}}
					backLabel="Fleet Vehicles"
				/>
			) : (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						height: "calc(100vh - 85px)",
						marginTop: theme.spacing(0.5),
					}}
				>
					<VehiclesTableToolbar
						onAddClick={() => {
							setOpenDialog(true);
						}}
						makeOptions={makeOptions}
						modelOptions={modelOptions}
						yearOptions={yearOptions}
						divisionOptions={departmentOptions}
						employeeOptions={employees}
						fundingOptions={fundingSources}
						setSearch={setSearch}
						vehicles={vehicles}
						resetFilters={resetFilters}
						filters={filters}
						onFilterChange={handleOnFilterChange}
						vehicleSvcStatus={vehicleSvcStatus}
					/>

					<Box
						sx={{
							flex: 1,
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<DataGridPro
							rows={vehicles}
							columns={columns}
							initialState={{
								pinnedColumns: { right: ["actions"] },
							}}
							getRowId={(row) => row.GUID}
							columnVisibilityModel={columnVisibilityModel}
							onColumnVisibilityModelChange={
								handleColumnVisibilityModelChange
							}
							pageSize={pageSize}
							rowsPerPageOptions={rowsPerPageOptions}
							pagination
							paginationMode="server"
							sortModel={sortModel}
							sortingMode="server"
							onRowClick={(params) => {
								setSelectedVehicleDetail(params.row);
							}}
							onSortModelChange={handleSortModelChange}
							rowCount={total}
							page={page}
							onPageChange={setPage}
							onPageSizeChange={setPageSize}
							loading={loading}
							disableSelectionOnClick
							onColumnOrderChange={handleColumnOrderChange}
							onColumnWidthChange={handleColumnWidthChange}
							sx={{
								...paginationStyle(theme, themeMode),
								color: theme.palette.colorFont,
								margin: 1,
							}}
						/>
					</Box>

					<AddEditVehicleDialog
						open={openDialog}
						onClose={() => {
							setOpenDialog(false);
							setSelectedVehicle(null);
						}}
						onSave={handleSaveVehicle}
						initialData={selectedVehicle}
						statusOptions={vehicleStatus}
						divisionOptions={departmentOptions}
						employeeOptions={employees}
						fundingSourceOptions={fundingSources}
						serviceRequirements={serviceRequirements || []}
					/>

					<DeleteConfirmationDialog
						open={openDeleteDialog}
						onClose={() => {
							setOpenDeleteDialog(false);
							setSelectedVehicle(null);
							setSelectedId(null);
						}}
						onConfirm={handleDelete}
						title="Delete Vehicle"
						message="Are you sure you want to delete this vehicle?"
					/>

					<CheckInOutVehicleDialog
						open={showCheckout}
						onClose={() => {
							setShowCheckout(false);
							setCheckoutVehicle(null);
						}}
						onSave={handleCheckInOut}
						vehicle={checkoutVehicle}
						checkoutMode={checkoutMode}
						employees={employees}
						divisionOptions={departmentOptions}
						canPerformInspection={true}
					/>

					{/* Inspection Dialog - opened after check-in/out when performInspection is checked */}
					<AddEditInspectionDialog
						open={openInspectionDialog}
						onClose={() => {
							setOpenInspectionDialog(false);
							setInspectionPreFill(null);
						}}
						onSave={handleSaveInspection}
						initialData={inspectionPreFill}
						vehicles={vehicleOptionsForInspection}
						employees={employees}
						checklistItems={checklistItems}
						inspectionTypeOptions={inspectionTypeOptions}
					/>
				</Box>
			)}
		</>
	);
};

export default Vehicles;
